import { useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { ArrowDropDown } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, DialogContentText, Menu, MenuItem } from '@mui/material'
import { useSnackbar } from 'notistack'

import MoveDeviceModal from '../../../components/change-folder-modals/move-device-modal'
import DeviceActivateModal from '../../../components/device-activate-modal'
import DeviceDeactivateModal from '../../../components/device-deactivate-modal'
import DeviceEditModal from '../../../components/device-edit-modal'
import DevicePauseModal from '../../../components/device-pause-modal'
import DeviceReturnModal from '../../../components/device-return-modal'
import DeviceUnpauseModal from '../../../components/device-unpause-modal'
import DialogWithTitle from '../../../components/dialog-with-title'
import ExternalLinkMenuItem from '../../../components/external-link-menu-item'
import { useHost } from '../../../providers/app-provider'
import { reactivateDevice, showDeviceMenuItems } from '../../../utils/helpers'
import useFolderTheme from '../../../utils/hooks/useFolderTheme'

interface IProps {
  device: IDeviceAPIObject
  setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>>
  fetchDevice: () => void
}

const DeviceActions = ({ device, setDevice, fetchDevice }: IProps) => {
  const { folderTheme } = useFolderTheme(device.folderId)

  const auth = useAuth()
  const host = useHost()
  const { enqueueSnackbar } = useSnackbar()

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [activateDeviceModal, setActivateDeviceModal] = useState<boolean>(false)
  const [editDeviceModal, setEditDeviceModal] = useState<boolean>(false)
  const [pauseModal, setPauseModal] = useState<boolean>(false)
  const [unpauseModal, setUnpauseModal] = useState<boolean>(false)
  const [returnModal, setReturnModal] = useState<boolean>(false)
  const [deactivateModal, setDeactivateModal] = useState<boolean>(false)
  const [valveConfirmModal, setValveConfirmModal] = useState<boolean>(false)
  const [moveModal, setMoveModal] = useState<boolean>(false)

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleReactivateClick = async () => {
    await reactivateDevice(device.id, host, auth.user?.access_token, enqueueSnackbar, fetchDevice)
    handleMenuClose()
  }

  const { showActivate, showEditDetails, showPause, showUnpause, showRMA, showDeactivate, resourcesLink, showReactivate /* , showValve */ } =
    showDeviceMenuItems(device)

  return (
    <>
      {(showEditDetails || showPause || showUnpause || showRMA || showDeactivate || resourcesLink) && (
        <Button onClick={(e) => setMenuAnchor(e.currentTarget)} endIcon={<ArrowDropDown />}>
          Actions
        </Button>
      )}

      <Menu
        id="filter-menu"
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {showActivate && (
          <MenuItem
            onClick={() => {
              setActivateDeviceModal(true)
              handleMenuClose()
            }}
          >
            Activate
          </MenuItem>
        )}

        {showEditDetails && (
          <MenuItem
            onClick={() => {
              setEditDeviceModal(true)
              handleMenuClose()
            }}
          >
            Edit Details
          </MenuItem>
        )}

        {resourcesLink && <ExternalLinkMenuItem label="View Resources" href={resourcesLink} onClick={handleMenuClose} />}

        {showReactivate && <MenuItem onClick={handleReactivateClick}>Reactivate</MenuItem>}

        {showPause && (
          <MenuItem
            onClick={() => {
              setPauseModal(true)
              handleMenuClose()
            }}
          >
            Pause
          </MenuItem>
        )}

        {showUnpause && (
          <MenuItem
            onClick={() => {
              setUnpauseModal(true)
              handleMenuClose()
            }}
          >
            Unpause
          </MenuItem>
        )}

        {showRMA && (
          <MenuItem
            onClick={() => {
              setReturnModal(true)
              handleMenuClose()
            }}
          >
            RMA
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            setMoveModal(true)
            handleMenuClose()
          }}
        >
          Move
        </MenuItem>

        {showDeactivate && (
          <MenuItem
            onClick={() => {
              setDeactivateModal(true)
              handleMenuClose()
            }}
          >
            Deactivate
          </MenuItem>
        )}
        {/* TODO commented out for now since Max said we may need to add it back;
         **remove this code entirely if the request to add it back does not come in a reasonable time
         */}
        {/* {showValve && (
          <div>
            <Divider />
            <ListSubheader sx={{ color: 'text.primary' }}>Shutoff Valve</ListSubheader>
            {device.currentValues.valve?.value === 'open' ? (
              <MenuItem
                onClick={() => {
                  setValveConfirmModal(true)
                  handleMenuClose()
                }}
                sx={{ paddingLeft: 4 }}
              >
                Close Valve
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  setValveConfirmModal(true)
                  handleMenuClose()
                }}
                sx={{ paddingLeft: 4 }}
              >
                Open Valve
              </MenuItem>
            )}
          </div>
        )} */}
      </Menu>

      <DialogWithTitle open={valveConfirmModal} setOpen={setValveConfirmModal} title="Confirm Action">
        <DialogContent>
          <DialogContentText>
            {device.currentValues.valve?.value === 'open'
              ? 'Closing the valve will turn off the water. Do you wish to continue?'
              : 'Opening the valve will turn on the water. Do you wish to continue?'}
          </DialogContentText>
          <DialogActions sx={{ marginTop: 2 }}>
            <Button variant="outlined" onClick={() => setValveConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => setValveConfirmModal(false)}>
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogWithTitle>

      <DevicePauseModal open={pauseModal} devices={[device]} onClose={() => setPauseModal(false)} onContinue={fetchDevice} />

      <DeviceUnpauseModal open={unpauseModal} devices={[device]} onClose={() => setUnpauseModal(false)} onContinue={fetchDevice} />

      <DeviceReturnModal open={returnModal} returnDevices={[device]} onClose={() => setReturnModal(false)} refetchView={fetchDevice} />

      <MoveDeviceModal open={moveModal} onClose={() => setMoveModal(false)} devices={[device]} refresh={fetchDevice} />

      <DeviceDeactivateModal open={deactivateModal} deactivateDevices={[device]} onClose={() => setDeactivateModal(false)} onContinue={fetchDevice} />

      <DeviceEditModal device={device} setDevice={setDevice} open={editDeviceModal} onClose={() => setEditDeviceModal(false)} folderTheme={folderTheme} />

      <DeviceActivateModal
        open={activateDeviceModal}
        device={device}
        deactivatedDevices={undefined}
        onClose={() => setActivateDeviceModal(false)}
        onContinue={fetchDevice}
        folderTheme={folderTheme}
      />
    </>
  )
}

export default DeviceActions
